import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Divider,
    useTheme,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // For styled bullet points
import ArticleIcon from '@mui/icons-material/Article'; // For section headers
import Header from '../components/Header';
import Footer from '../components/Footer';

interface PageProps {
    mode: 'light' | 'dark';
    setMode: (mode: 'light' | 'dark') => void;
}

// Custom component for a single term section
const TermSection = ({ title, content, children }: { title: string, content?: string | React.ReactNode, children?: React.ReactNode }) => (
    <Box mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <ArticleIcon sx={{ mr: 1.5, fontSize: '1.2em' }} />
            {title}
        </Typography>
        {content && (
            <Typography variant="body1" color="text.secondary" mb={2}>
                {content}
            </Typography>
        )}
        {children}
    </Box>
);

// Custom component for a list of bullet points using MUI List
const TermList = ({ items, mb = 3 }: { items: string[], mb?: number }) => (
    <List sx={{ pt: 0, pb: mb, pl: 2 }}>
        {items.map((item, index) => (
            <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: '30px' }}>
                    <CheckCircleOutlineIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary={item} primaryTypographyProps={{ color: 'text.secondary', lineHeight: 1.6 }} />
            </ListItem>
        ))}
    </List>
);

const TermsPage = ({ mode, setMode }: PageProps) => {
    const theme = useTheme();
    // Assuming isLoggedIn is used only for the Header component logic
    const [isLoggedIn] = useState(false);

    return (
        <>
            <Header  />

            {/* Main content box: Use theme background color and controlled padding */}
            <Box sx={{ backgroundColor: theme.palette.background.default, py: { xs: 6, md: 10 } }}>
                <Container maxWidth="md">

                    {/* Title and Introduction */}
                    <Box sx={{ pb: 4 }}>
                        <Typography variant="h3" fontWeight="bold" gutterBottom color="text.primary">
                            Terms of Service
                        </Typography>

                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                            These Terms of Service (“Terms”) govern your access and use of the **WorkHorse**
                            platform (“WorkHorse”, “we”, “our”, “us”). By using our website, application,
                            accounts, listings, services, or any part of the WorkHorse system, you agree
                            to comply with these Terms and our incorporated policies.
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    {/* 1. Definitions */}
                    <TermSection title="1. Definitions">
                        <TermList items={[
                            '“WorkHorse Platform” refers to the website, mobile app, and online systems operated by WorkHorse SG Pte Ltd.',
                            '“Account(s)” includes both Master Accounts and Admin/Sub-Accounts.',
                            '“User”, “you” refers to any individual or business using the platform.',
                            '“Listings” refer to vehicle or advertisement listings you publish.',
                            '“Services” refer to all platform features including listings, promotions, marketing tools, subscriptions, and related functions.',
                            '“Charges” refers to all fees payable for listings, subscriptions, or other platform services.',
                        ]} />
                    </TermSection>

                    {/* 2. Account Usage */}
                    <TermSection title="2. Account Usage" content="By registering an account on the WorkHorse Platform, you agree to:">
                        <TermList items={[
                            'Maintain accurate information',
                            'Keep all login credentials secure',
                            'Ensure only authorised personnel access your account',
                            'Be solely responsible for all activity under your account',
                        ]} />
                        <Typography variant="body1" color="text.secondary">
                            WorkHorse reserves the right to suspend or terminate your account for violations
                            of these Terms or misuse of the platform.
                        </Typography>
                    </TermSection>

                    {/* 3. Technical Requirements */}
                    <TermSection title="3. Technical Requirements and Permissions" content="Using the platform may require a stable internet connection and a compatible
                        device. By using WorkHorse, you consent to the platform accessing necessary
                        device features including:">
                        <TermList items={[
                            'Camera',
                            'Push notifications',
                            'Location services',
                            'Device data required to operate features',
                        ]} />
                    </TermSection>

                    {/* 4. Updates */}
                    <TermSection title="4. Updates and System Upgrades" content="WorkHorse may release updates, patches, or new versions of the platform. You
                        are responsible for installing updates to maintain functionality. Certain features may not work if updates are not applied." />

                    {/* 5. Intellectual Property */}
                    <TermSection title="5. Intellectual Property" content={<>
                        All intellectual property, content, designs, images, and materials created by or
                        for WorkHorse remain the exclusive property of **WorkHorse SG Pte Ltd**. You may
                        not reproduce or use them without written permission.
                        <br /><br />
                        For listings you upload, you grant WorkHorse a perpetual, worldwide, royalty-free
                        licence to use, reproduce, display, and adapt such content for platform operations.
                    </>} />

                    {/* 6. Term & Termination */}
                    <TermSection title="6. Term and Termination" content="These Terms take effect when you begin using the platform. WorkHorse may
                        suspend or terminate access at any time for breaches, misuse, or unlawful activity.
                        Termination does not affect rights accrued prior to termination." />

                    {/* 7. Platform Access */}
                    <TermSection title="7. Platform Access" content="We do not guarantee uninterrupted, secure, or error-free access. WorkHorse is
                        not liable for downtime, maintenance, system failures, or data loss." />

                    {/* 8. Indemnity */}
                    <TermSection title="8. Indemnity" content="You agree to indemnify and hold harmless WorkHorse, its employees,
                        contractors, and partners from any claims arising from:">
                        <TermList items={[
                            'Your use of the platform',
                            'Any content you upload or publish',
                            'Any breach of these Terms',
                        ]} />
                    </TermSection>

                    {/* 9. Exclusion of Liability */}
                    <TermSection title="9. Exclusion of Liability" content={<>
                        WorkHorse is not liable for:
                        <TermList items={[
                            'Inaccurate, incomplete, or misleading listings',
                            'Loss of profits, data, or business opportunities',
                            'Indirect, incidental, or consequential damages',
                        ]} mb={1} />
                        If liability is required by law, it is limited to the total charges paid to WorkHorse.
                    </>} />

                    {/* 10. Warranties */}
                    <TermSection title="10. Representations and Warranties" content="You confirm you are legally authorised to use the platform and that your use does
                        not violate any laws, regulations, or agreements." />

                    {/* 11. General */}
                    <TermSection title="11. General Terms">
                        <TermList items={[
                            'No partnership or employment relationship is created.',
                            'You may not assign your rights without permission.',
                            'WorkHorse may amend these Terms with notice.',
                            'Notices may be delivered by email, SMS, WhatsApp, or platform messages.',
                            'Invalid clauses do not affect the remaining Terms.',
                            'These Terms are governed by the laws of **Singapore**.',
                        ]} />
                    </TermSection>

                    {/* 12. Listings */}
                    <TermSection title="12. Listing Requirements" content="You confirm that:">
                        <TermList items={[
                            'All vehicles listed are genuinely available',
                            'All listing information is accurate and lawful',
                            'No false, misleading, defamatory, or infringing content is uploaded',
                            'All uploaded files are free from harmful code',
                        ]} />
                        <Typography variant="body1" color="text.secondary">
                            WorkHorse may reject or remove listings that violate these requirements.
                        </Typography>
                    </TermSection>

                    {/* 13. Fees */}
                    <TermSection title="13. Fees and Payment Terms" content="All fees must be paid according to WorkHorse’s prevailing rates. Invoices are due
                        within **30 days**. Late payments may result in account suspension and interest
                        charges of **8% per annum**." />

                    {/* 14. Advertising */}
                    <TermSection title="14. Advertising and Promotion" content="WorkHorse may display your listings in its advertising, marketing, or promotional
                        campaigns without additional consent or compensation. We may edit listing media
                        for quality, provided the content is not misrepresented." />

                    {/* 15. PDPA */}
                    <TermSection title="15. Personal Data Protection (PDPA)" content={<>
                        WorkHorse collects, uses, and discloses personal data in accordance with the
                        PDPA and our Privacy Policy. You confirm that all data provided is accurate and
                        that you have obtained necessary consent. For PDPA enquiries, contact:
                        <Box mt={1}>
                            <Typography variant="body1" color="primary.main" fontWeight="bold">
                                privacy@workhorse.sg
                            </Typography>
                        </Box>
                    </>} />

                    {/* 16. Consent for Uploads */}
                    <TermSection title="16. Upload & Auto-Generation Consent" content="By uploading documents or media, you consent to WorkHorse importing, extracting,
                        analysing, and using the data to generate documents, listings, or other platform
                        content. You remain responsible for the accuracy of all uploaded materials." />

                    {/* 17. Dispute Resolution */}
                    <TermSection title="17. Dispute Resolution" content="Parties shall attempt amicable resolution within 30 days of any dispute. If unresolved,
                        the matter shall be submitted to the courts of **Singapore**, which shall have exclusive
                        jurisdiction. WorkHorse may seek urgent injunctive relief for breaches of confidentiality
                        or intellectual property." />

                    <Divider sx={{ my: 4 }} />

                    {/* Conclusion */}
                    <Box sx={{ pb: 4 }}>
                        <Typography variant="body1" color="text.secondary" mb={2} sx={{ fontSize: '1.1rem' }}>
                            For enquiries about these Terms, contact us at: **support@workhorse.sg**
                        </Typography>
                    </Box>

                </Container>
            </Box>

            <Footer />
        </>
    );
};

export default TermsPage;